import React, { CSSProperties, ReactNode, useCallback } from 'react';
import {
   FlexItemDefinition, FlexItemMap,
   FlexTemplateDefinition,
   GapProperty
} from './FlexTemplatePanel';
import { FlexPanel } from '../../flexpanel';
import { Property } from 'csstype';
import { TemplateAreaProps } from '../TemplateArea';

export interface MappedFlexPanelsProps {
   flexTemplate: FlexTemplateDefinition;
   hgap?: GapProperty;
   vgap?: GapProperty;
   children: ReactNode;
}

export const MappedFlexPanels = (props: MappedFlexPanelsProps) => {

   const childAreaMap = getChildAreaMap(props.children);

   const autoOverflowAreas = props.flexTemplate.autoOverflowAreas;

   return (<>
      {
         props.flexTemplate.rows.map((flexPanelDefinition, index) => {
            if (getChildCount(childAreaMap, flexPanelDefinition) > 0) {
               return <FlexPanelRow id={`flex-row--${index}`}
                                    key={`flex-row--${index}`}
                                    childAreaMap={childAreaMap}
                                    flexPanelDefinition={flexPanelDefinition}
                                    autoOverflowAreas={autoOverflowAreas}
                                    hgap={props.hgap}
                                    vgap={props.vgap}
               />
            }
         })
      }
   </>);
};

type ChildAreaMap = {
   [areaName: string]: React.ReactElement
};

const getChildAreaMap = (children: ReactNode) => {
   const childAreaMap: ChildAreaMap = {};

   React.Children.forEach(children, (child, index) => {
      const reactElement = child as React.ReactElement;
      if (reactElement.props.areaName) {
         childAreaMap[reactElement.props.areaName] = reactElement;
      }
   });

   return childAreaMap;
};

const getChildCount = (childAreaMap: ChildAreaMap, flexPanelDefinition: FlexItemDefinition) => {

   const flexItemMap = flexPanelDefinition.flexItems || {};

   let count = 0;
   for (const areaName in flexItemMap) {
      if (Object.prototype.hasOwnProperty.call(flexItemMap, areaName)) {
         const templateArea = childAreaMap[areaName];
         if (templateArea === undefined) {
            count += getChildCount(childAreaMap, flexItemMap[areaName]);
         }
         else {
            count++;
         }
      }
   }
   return count;
};

const getStyleFromFlexItemProps = (templateAreaProps: TemplateAreaProps | null,
                                   flexItemDefinition: FlexItemDefinition,
                                   flexDirection: Property.FlexDirection) => {

   const style:CSSProperties = {};

   const {
      order,
      flex,
      width,
      height,
      minWidth,
      minHeight,
      maxWidth,
      maxHeight
   } = flexItemDefinition;

   const {
      align = flexItemDefinition.alignSelf,
      justify = flexItemDefinition.justifySelf
   } = templateAreaProps || {};

   if (align) {
      style.alignSelf = align;
   }

   if (justify) {
      if (flexDirection === 'row') {
         if (justify === 'start') {
            style.marginRight = 'auto';
         }
         else if (justify === 'end') {
            style.marginLeft = 'auto';
         }
      }
      else {
         if (justify === 'start') {
            style.marginBottom = 'auto';
         }
         else if (justify === 'end') {
            style.marginTop = 'auto';
         }
      }
   }

   if (order) {
      style.order = order;
   }

   if (flex) {
      style.flex = flex;
   }

   if (width) {
      style.width = width;
   }

   if (height) {
      style.height = height;
   }

   if (minWidth) {
      style.minWidth = minWidth;
   }

   if (minHeight) {
      style.minHeight = minHeight;
   }

   if (maxWidth) {
      style.maxWidth = maxWidth;
   }

   if (maxHeight) {
      style.maxHeight = maxHeight;
   }

   if (flexItemDefinition.flexDirection ||
       flexItemDefinition.flexWrap ||
       flexItemDefinition.justifyContent ||
       flexItemDefinition.alignItems ||
       flexItemDefinition.alignContent) {

      style.display = 'flex';

      style.alignItems = flexItemDefinition.alignItems;
      style.flexDirection = flexItemDefinition.flexDirection;
      style.flexWrap = flexItemDefinition.flexWrap;
      style.justifyContent = flexItemDefinition.justifyContent;
      style.alignContent = flexItemDefinition.alignContent;
   }

   return style;
};

const applyFlexTemplateAreaPropsToChildren = (childAreaMap: ChildAreaMap,
                                              flexItemMap: FlexItemMap,
                                              flexDirection: Property.FlexDirection,
                                              autoOverflowAreas?: boolean | string[],
                                              hgap?: GapProperty,
                                              vgap?: GapProperty) => {
   const childrenToRender = [];

   for (const areaName in flexItemMap) {
      if (Object.prototype.hasOwnProperty.call(flexItemMap, areaName)) {
         let templateArea = childAreaMap[areaName];
         const flexItemDefinition = flexItemMap[areaName];

         if (templateArea === undefined && getChildCount(childAreaMap, flexItemMap[areaName]) <= 0) {
            continue;
         }

         if (templateArea === undefined) {
            templateArea = <FlexPanelRow id={areaName}
                                         childAreaMap={childAreaMap}
                                         flexPanelDefinition={flexItemDefinition}
                                         autoOverflowAreas={autoOverflowAreas}
                                         hgap={hgap}
                                         vgap={vgap}/>
         }

         let overflowY;
         if (autoOverflowAreas && (autoOverflowAreas === true || autoOverflowAreas.indexOf(areaName) >= 0)) {
            overflowY = 'auto';
         }

         const propsStyle = getStyleFromFlexItemProps(templateArea.props, flexItemDefinition, flexDirection);

         const style: CSSProperties = {
            ...templateArea.props.style,
            overflowY: overflowY,
            ...propsStyle
         };

         childrenToRender.push(React.cloneElement(templateArea,
                                                  {
                                                     style: style,
                                                     className: templateArea.props.className,
                                                     areaName: areaName,
                                                     key: areaName
                                                  }));
      }
   }

   return childrenToRender;
};

interface FlexPanelRowProps {
   id: string;
   childAreaMap: ChildAreaMap;
   flexPanelDefinition: FlexItemDefinition;
   autoOverflowAreas?: boolean | string[];
   hgap?: GapProperty;
   vgap?: GapProperty;
}

const FlexPanelRow = (props: FlexPanelRowProps) => {

   const flexPanelDefinition = props.flexPanelDefinition;

   const flexDirection = flexPanelDefinition.flexDirection || 'row';

   const style = getStyleFromFlexItemProps(null, flexPanelDefinition, flexDirection);

   const applyToChildren = useCallback(() => {
      return applyFlexTemplateAreaPropsToChildren(props.childAreaMap,
                                           flexPanelDefinition.flexItems || {},
                                           flexDirection,
                                           props.autoOverflowAreas,
                                           props.hgap,
                                           props.vgap)

   },[props.childAreaMap, flexPanelDefinition.flexItems, flexDirection, props.autoOverflowAreas, props.hgap, props.vgap]);

   return (<FlexPanel
      width={'auto'}
      hgap={props.hgap}
      vgap={props.vgap}
      verticalFill={false}
      flexDirection={flexDirection}
      justifyContent={flexPanelDefinition.justifyContent}
      alignItems={flexPanelDefinition.alignItems || 'stretch'}
      alignContent={flexPanelDefinition.alignContent}
      flexWrap={flexPanelDefinition.flexWrap}
      style={style}
      key={props.id}
   >
      {applyToChildren()}
   </FlexPanel>)
};
